import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { getVesselBoilerOverview } from "../../../../services/vesselMachineryService";
import { Zap, Package2, Settings, Cloud } from "lucide-react";

const BoilerData = () => {
  const { startDate, endDate } = useSelector((state) => state.dateRange);
  const { currentVessel } = useSelector((state) => state.vesselOverview);
  const { selectedVoyage } = useSelector((state) => state.voyageFilter);
  const [boilerOverview, setBoilerOverview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Default empty data structure
  const defaultBoilerData = {
    cargoHeating: { value: "0.0" },
    cargoDischarge: { value: "0.0" },
    generalService: { value: "0.0" },
    totalFuelConsumption: { value: "0.0" },
  };

  useEffect(() => {
    const fetchBoilerOverview = async () => {
      if (!currentVessel?._id) {
        setBoilerOverview(defaultBoilerData);
        return;
      }

      if (!selectedVoyage && (!startDate || !endDate)) {
        setBoilerOverview(defaultBoilerData);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await getVesselBoilerOverview(
          currentVessel._id,
          selectedVoyage ? null : startDate,
          selectedVoyage ? null : endDate,
          selectedVoyage?._id || null
        );

        console.log("API Response BoilerData:", response);

        if (response?.status === "success") {
          setBoilerOverview(response.data || defaultBoilerData);
        } else {
          setBoilerOverview(defaultBoilerData);
          setError(response?.message || "Failed to fetch boiler overview data");
        }
      } catch (err) {
        console.error("API Error:", err);
        setError(err.message);
        setBoilerOverview(defaultBoilerData);
      } finally {
        setLoading(false);
      }
    };

    fetchBoilerOverview();
  }, [currentVessel, startDate, endDate, selectedVoyage]);

  // Show loading state
  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 p-4 bg-gray-50 rounded-xl">
        {[1, 2, 3, 4].map((item) => (
          <div
            key={item}
            className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 animate-pulse"
          >
            <div className="h-4 bg-gray-200 rounded w-1/3 mb-2"></div>
            <div className="h-8 bg-gray-200 rounded w-1/2 mb-2"></div>
            <div className="h-2 bg-gray-200 rounded w-full mt-3"></div>
          </div>
        ))}
      </div>
    );
  }

  // Helper function to get data with fallbacks to default
  const getData = (key) => {
    return boilerOverview?.[key]?.value || "0.0";
  };

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {/* Error message */}
      {error && (
        <div className="col-span-full bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
          <p className="text-yellow-800 text-sm">API Error: {error}</p>
        </div>
      )}

      {/* Cargo Heating Tile */}
      <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-200">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-blue-50 rounded-lg">
            <Zap className="w-5 h-5 text-blue-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide truncate">
              Cargo Heating
            </h3>
            <div className="flex items-baseline mt-1">
              <span className="text-2xl font-bold text-gray-900">
                {getData("cargoHeating")}
              </span>
              <span className="ml-1 text-sm text-gray-500">MT</span>
            </div>
          </div>
        </div>
      </div>

      {/* Cargo Discharge Tile */}
      <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-200">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-green-50 rounded-lg">
            <Package2 className="w-5 h-5 text-green-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide truncate">
              Cargo Discharge
            </h3>
            <div className="flex items-baseline mt-1">
              <span className="text-2xl font-bold text-gray-900">
                {getData("cargoDischarge")}
              </span>
              <span className="ml-1 text-sm text-gray-500">MT</span>
            </div>
          </div>
        </div>
      </div>

      {/* General Service Tile */}
      <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-200">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-purple-50 rounded-lg">
            <Settings className="w-5 h-5 text-purple-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide truncate">
              General Service
            </h3>
            <div className="flex items-baseline mt-1">
              <span className="text-2xl font-bold text-gray-900">
                {getData("generalService")}
              </span>
              <span className="ml-1 text-sm text-gray-500">MT</span>
            </div>
          </div>
        </div>
      </div>

      {/* Auxiliary Steam Tile */}
      <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-200">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-orange-50 rounded-lg">
            <Cloud className="w-5 h-5 text-orange-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide truncate">
              Total Fuel Consumption
            </h3>
            <div className="flex items-baseline mt-1">
              <span className="text-2xl font-bold text-gray-900">
                {getData("totalFuelConsumption")}
              </span>
              <span className="ml-1 text-sm text-gray-500">MT</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BoilerData;